// src/services/idDocumentService.ts
import { firestoreService } from '@/utils/firestoreService'
import type { IDDocument } from '@/types/IDDocument'
import { IDDocumentModel } from '@/models/IDDocumentModel'
import { usePersonStore } from '@/stores/v2/person'

const COLLECTION = 'id_documents'

export const idDocumentService = {
  get: async (id: string) => firestoreService.get<IDDocument>(COLLECTION, id),

  listByPerson: async (personId: string) =>
    firestoreService.query<IDDocument>(
      COLLECTION,
      [
        { field: 'personId', op: '==', value: personId },
        { field: 'isDeleted', op: '==', value: false },
      ],
      ['updatedAt', 'desc'],
    ),

  findByBarcodeHash: async (barcodeHash: string) =>
    firestoreService.query<IDDocument>(
      COLLECTION,
      [
        { field: 'barcodeHash', op: '==', value: barcodeHash },
        { field: 'isDeleted', op: '==', value: false },
      ],
      ['updatedAt', 'desc'],
      1,
    ),

  add: async (doc: IDDocument): Promise<IDDocument> => {
    const id = await firestoreService.add<IDDocument>(COLLECTION, doc)
    const saved = await firestoreService.get<IDDocument>(COLLECTION, id)
    return { ...(doc as IDDocument), id, ...(saved || {}) }
  },

  update: async (doc: IDDocument): Promise<IDDocument> => {
    if (!doc.id) throw new Error('IDDocument ID is required for update')
    await firestoreService.set<IDDocument>(COLLECTION, { ...doc, id: doc.id })
    const saved = await firestoreService.get<IDDocument>(COLLECTION, doc.id)
    return saved as IDDocument
  },

  softDelete: async (id: string) => {
    await firestoreService.update<IDDocument>(COLLECTION, id, {
      isDeleted: true,
      updatedAt: new Date(),
    })
  },

  /**
   * Get current ID document for a person
   */
  async getCurrentIdDocument(personId: string): Promise<IDDocument | null> {
    try {
      const personStore = usePersonStore()
      const person = await personStore.get(personId)

      if (!person?.currentIdDocumentId) {
        return null
      }

      return await this.get(person.currentIdDocumentId)
    } catch (error) {
      console.error('Failed to load current ID document:', error)
      return null
    }
  },

  /**
   * Save ID document images and associate with person
   */
  async saveIdDocumentImages(
    personId: string,
    frontImageUrl: string,
    backImageUrl?: string,
  ): Promise<IDDocument> {
    try {
      const personStore = usePersonStore()

      // Get current document or create new one
      let currentDoc = await this.getCurrentIdDocument(personId)

      if (currentDoc?.id) {
        // Update existing document
        const updatedDoc = {
          ...currentDoc,
          frontImageUrl,
          backImageUrl: backImageUrl || undefined,
          updatedAt: new Date(),
        }

        await this.update(updatedDoc)

        // Update person with photo URL for backward compatibility
        await personStore.update(personId, {
          photoIdUrl: frontImageUrl,
          updatedAt: new Date(),
        })

        return updatedDoc
      } else {
        // Create new document
        const newDoc = IDDocumentModel.create({
          personId,
          type: 'driver_license',
          frontImageUrl,
          backImageUrl,
        })

        const createdDoc = await this.add(newDoc)

        // Update person with currentIdDocumentId and photo URL
        await personStore.update(personId, {
          currentIdDocumentId: createdDoc.id,
          photoIdUrl: frontImageUrl,
          updatedAt: new Date(),
        })

        return createdDoc
      }
    } catch (error) {
      console.error('Failed to save ID document:', error)
      throw new Error('Failed to save ID document')
    }
  },

  /**
   * Create ID document from scanned data
   */
  async createIdDocumentFromScan(
    personId: string,
    frontImageUrl: string,
    backImageUrl?: string,
    barcodeHash?: string,
  ): Promise<IDDocument> {
    try {
      const doc = IDDocumentModel.create({
        personId,
        type: 'driver_license',
        frontImageUrl,
        backImageUrl,
        barcodeHash,
      })

      const createdDoc = await this.add(doc)

      // Update person with current document reference
      const personStore = usePersonStore()
      await personStore.update(personId, {
        currentIdDocumentId: createdDoc.id,
        photoIdUrl: frontImageUrl,
        updatedAt: new Date(),
      })

      return createdDoc
    } catch (error) {
      console.error('Failed to create ID document from scan:', error)
      throw new Error('Failed to create ID document from scan')
    }
  },

  /**
   * Update person with ID photo URL for backward compatibility
   */
  async updatePersonPhotoUrl(personId: string, photoUrl: string): Promise<void> {
    try {
      const personStore = usePersonStore()
      await personStore.update(personId, {
        photoIdUrl: photoUrl,
        updatedAt: new Date(),
      })
    } catch (error) {
      console.error('Failed to update person photo URL:', error)
      throw new Error('Failed to update person photo URL')
    }
  },
}
